import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Routes,
  Route,
  NavLink,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ReactComponent as LoginLogo } from "../../../assets/logos/pbxscribe-logo-white.svg";
import { ReactComponent as DashboardIcon } from "../../../assets/icons/dashboard.svg";
import { ReactComponent as UserIcon } from "../../../assets/icons/user.svg";
import { ReactComponent as NumberMgmntIcon } from "../../../assets/icons/NumberMgmnt.svg";
import { ReactComponent as RolesIcon } from "../../../assets/icons/roles.svg";
import { ReactComponent as ConfigurationIcon } from "../../../assets/icons/configuration.svg";
import { ReactComponent as LogsIcon } from "../../../assets/icons/log.svg";
import { ReactComponent as BillingIcon } from "../../../assets/icons/billing.svg";
import { ReactComponent as ReferralIcon } from "../../../assets/icons/referral.svg";
import { ReactComponent as NotifyIcon } from "../../../assets/icons/notification.svg";
import { ReactComponent as SupportIcon } from "../../../assets/icons/support.svg";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import Header from "./Header";
import Footer from "./Footer";
import Breadcrumb from "./Breadcrumb";
//routes
import Dashboard from "../../../features/dashboard/components/Dashboard";
import Logs from "../../../features/logs/components/Logs";
import Billing from "../../../features/billing/components/Billing";
import Notifications from "../../../features/notifications/components/Notifications";
import ReferAndEarn from "../../../features/referral/components/ReferAndEarn";
import PBXConfig from "../../../features/pbx-config/components/PBXConfig";
import HelpAndSupport from "../../../features/support/components/HelpAndSupport";
import Documentation from "../../../features/support/components/Documentation";
import SystemStatus from "../../../features/support/components/SystemStatus";
import ContactSupport from "../../../features/support/components/ContactSupport";
import ContactSupportCase from "../../../features/support/components/ContactSupportCase";
import UserManagement from "../../../features/users/components/UserManagement";
import AdminManagement from "../../../features/admin/components/AdminManagement";
import "./AuthenticatedLayout.css";
import NumberManagement from "../../../features/numbers/components/NumberManagement";
import { selectCurrentUser } from "../../../features/auth/store/authSlice";
import { isAdmin, isManager, isViewer } from "../../utils/roles";

const LAST_AUTH_ROUTE_KEY = "pbx_last_auth_route";
const RESTORABLE_AUTH_PATHS = new Set([
  "/dashboard",
  "/admin-management",
  "/logs",
  "/billing",
  "/user-management",
  "/number-management",
  "/notifications",
  "/referral",
  "/configuration",
  "/support",
  "/documentation",
  "/support/documentation",
  "/support/system-status",
  "/support/contact-support",
  "/support/contact-support/:id",
]);

const roleAllows = (role, allowedRoles) => {
  if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) return true;
  return allowedRoles.includes(role);
};

const ALL_ROLES = ["admin", "manager", "viewer"];

const ROUTE_ROLE_MAP = {
  "/dashboard": ALL_ROLES,
  "/admin-management": ["manager", "admin"],
  "/logs": ALL_ROLES,
  "/billing": ALL_ROLES,
  "/user-management": ["manager", "admin"],
  "/number-management": ALL_ROLES,
  "/notifications": ALL_ROLES,
  "/referral": ALL_ROLES,
  "/configuration": ALL_ROLES,
  "/support": ALL_ROLES,
  "/support/contact-support/:id": ALL_ROLES,
  "/documentation": ALL_ROLES,
  "/support/documentation": ALL_ROLES,
  "/support/system-status": ALL_ROLES,
  "/support/faqs": ALL_ROLES,
};

const isRouteAllowed = (role, path) => {
  const allowedRoles = ROUTE_ROLE_MAP[path];
  return roleAllows(role, allowedRoles);
};

const RestoreLastAuthRoute = ({ currentRole }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const savedRoute = sessionStorage.getItem(LAST_AUTH_ROUTE_KEY);
    if (
      savedRoute &&
      RESTORABLE_AUTH_PATHS.has(savedRoute) &&
      isRouteAllowed(currentRole, savedRoute)
    ) {
      navigate(savedRoute, { replace: true });
      return;
    }

    navigate("/dashboard", { replace: true });
  }, [currentRole, navigate]);

  return null;
};

const RoleRoute = ({
  currentRole,
  allowedRoles,
  fallbackPath = "/dashboard",
  children,
}) => {
  if (roleAllows(currentRole, allowedRoles)) {
    return children;
  }

  return <Navigate to={fallbackPath} replace />;
};
// Inner component that uses Router context
const AuthenticatedLayoutContent = ({ handleSignOut, isActionLoading }) => {
  const currentUser = useSelector(selectCurrentUser);
  const currentRole = isAdmin(currentUser?.user_role)
    ? "admin"
    : isManager(currentUser?.user_role)
      ? "manager"
      : isViewer(currentUser?.user_role)
        ? "viewer"
        : "viewer";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const location = useLocation();
  const mainContentRef = React.useRef(null);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (RESTORABLE_AUTH_PATHS.has(location.pathname)) {
      sessionStorage.setItem(LAST_AUTH_ROUTE_KEY, location.pathname);
    }
  }, [location.pathname]);

  // Toggle sidebar expansion with closing animation
  const toggleSidebar = () => {
    if (isSidebarExpanded) {
      // Start closing animation
      setIsClosing(true);
      setTimeout(() => {
        setIsSidebarExpanded(false);
        setIsClosing(false);
      }, 200); // Match CSS transition duration
    } else {
      setIsSidebarExpanded(true);
    }
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "unset";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // Navigation items configuration
  const navItems = [
    { path: "/dashboard", label: "Dashboard", Icon: DashboardIcon, allowedRoles: ROUTE_ROLE_MAP["/dashboard"] },
    {
      path: "/user-management",
      label: "User Management",
      Icon: UserIcon,
      allowedRoles: ["manager", "admin"],
    },
    {
      path: "/number-management",
      label: "Number Management",
      Icon: NumberMgmntIcon,
      allowedRoles: ROUTE_ROLE_MAP["/number-management"],
    },
    {
      path: "/admin-management",
      label: "Admin Management",
      Icon: RolesIcon,
      allowedRoles: ["admin", "manager"],
    },
    {
      path: "/configuration",
      label: "PBX Configuration",
      Icon: ConfigurationIcon,
      allowedRoles: ROUTE_ROLE_MAP["/configuration"],
    },
    { path: "/logs", label: "Logs", Icon: LogsIcon, allowedRoles: ROUTE_ROLE_MAP["/logs"] },
    {
      path: "/billing",
      label: "Billing",
      Icon: BillingIcon,
      allowedRoles: ["manager", "admin", "viewer"],
    },
    {
      path: "/referral",
      label: "Referral",
      Icon: ReferralIcon,
      allowedRoles: ROUTE_ROLE_MAP["/referral"],
    },
    { path: "/notifications", label: "Notifications", Icon: NotifyIcon, allowedRoles: ROUTE_ROLE_MAP["/notifications"] },
    { path: "/support", label: "Help & Support", Icon: SupportIcon, allowedRoles: ROUTE_ROLE_MAP["/support"] },
  ];

  const visibleNavItems = navItems.filter((item) =>
    roleAllows(currentRole, item.allowedRoles),
  );

  // Helper function for nav link className
  const getNavLinkClassName = (isActive) =>
    `flex items-center gap-2 rounded-lg px-2 py-1 text-xs font-semibold transition ${
      isActive
        ? "bg-white text-primary"
        : "text-white hover:bg-white hover:text-primary"
    }`;

  return (
    <div className="min-h-screen bg-slate-50  text-slate-900 ">
      <div className="flex h-screen">
        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar - Desktop & Mobile */}
        <aside
          className={`sidebar-container fixed lg:static bg-primary h-full w-64 flex-col border-r border-slate-200 shadow-sm z-50 transform transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0 lg:flex ${
            isSidebarExpanded ? "sidebar-expanded" : ""
          } ${isClosing ? "sidebar-closing" : ""}`}
        >
          {/* Mobile Menu Header */}
          <div className="sidebar-header flex items-center justify-between border-b border-slate-100 px-4 py-4 lg:px-6 lg:py-5">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={toggleSidebar}
            >
              <LoginLogo className="sidebar-logo-full" />
              <img
                src="/images/logo-pbxscribe-favicon.svg"
                alt="Logo"
                className="sidebar-logo-icon hidden w-10 h-10"
              />
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden text-white hover:opacity-70 transition-opacity"
              aria-label="Close menu"
            >
              <CloseIcon />
            </button>
          </div>

          <nav className="flex flex-1 flex-col gap-1 px-4 py-4 overflow-y-auto">
            {visibleNavItems.map(({ path, label, Icon }) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `group nav-item ${getNavLinkClassName(isActive)}`
                }
              >
                {({ isActive }) => (
                  <>
                    <div
                      className="nav-icon rounded-lg p-1 transition"
                      data-active={isActive ? "true" : "false"}
                    >
                      <Icon className="group-hover:scale-[1.05] group-hover:rotate-6 transitions-all duration-300 " />
                    </div>
                    <span className="nav-label">{label}</span>
                    <span className="nav-tooltip hidden lg:block">{label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>
        <div className="flex flex-1 flex-col w-full lg:w-auto overflow-hidden min-w-0">
          {/* Mobile Menu Button */}
          <div className="lg:hidden fixed top-4 left-4 z-30">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="w-10 h-10 rounded-lg bg-primary text-white flex items-center justify-center hover:opacity-80 transition-opacity shadow-md"
              aria-label="Open menu"
            >
              <MenuIcon />
            </button>
          </div>
          <Header
            handleSignOut={handleSignOut}
            isActionLoading={isActionLoading}
          />
          <Breadcrumb />

          <main
            ref={mainContentRef}
            className="flex-1 overflow-y-auto bg-slate-50 flex flex-col min-w-0"
          >
            <div className="flex-1 px-4 py-6 sm:px-6">
              <Routes>
                <Route
                  path="/"
                  element={<RestoreLastAuthRoute currentRole={currentRole} />}
                />
                <Route
                  path="/dashboard"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/dashboard"]}>
                      <Dashboard />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/admin-management"
                  element={
                    <RoleRoute
                      currentRole={currentRole}
                      allowedRoles={ROUTE_ROLE_MAP["/admin-management"]}
                    >
                      <AdminManagement />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/logs"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/logs"]}>
                      <Logs />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <RoleRoute
                      currentRole={currentRole}
                      allowedRoles={ROUTE_ROLE_MAP["/billing"]}
                    >
                      <Billing />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/user-management"
                  element={
                    <RoleRoute
                      currentRole={currentRole}
                      allowedRoles={ROUTE_ROLE_MAP["/user-management"]}
                    >
                      <UserManagement />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/number-management"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/number-management"]}>
                      <NumberManagement />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/notifications"]}>
                      <Notifications />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/referral"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/referral"]}>
                      <ReferAndEarn />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/configuration"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/configuration"]}>
                      <PBXConfig />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/support"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/support"]}>
                      <HelpAndSupport />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/support/contact-support/:id"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/support/contact-support/:id"]}>
                      <ContactSupportCase />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/documentation"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/documentation"]}>
                      <Documentation />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/support/documentation"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/support/documentation"]}>
                      <Documentation />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/support/system-status"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/support/system-status"]}>
                      <SystemStatus />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/support/faqs"
                  element={
                    <RoleRoute currentRole={currentRole} allowedRoles={ROUTE_ROLE_MAP["/support/faqs"]}>
                      <ContactSupport />
                    </RoleRoute>
                  }
                />
                <Route
                  path="*"
                  element={<Navigate to="/dashboard" replace />}
                />
              </Routes>
            </div>
            <Footer />
          </main>
        </div>
      </div>
    </div>
  );
};

const AuthenticatedLayout = ({ handleSignOut, isActionLoading }) => {
  return (
    <AuthenticatedLayoutContent
      handleSignOut={handleSignOut}
      isActionLoading={isActionLoading}
    />
  );
};

export default AuthenticatedLayout;
